/**
 * How a chart writes its numbers — on an axis, a value label and a tooltip —
 * as `Intl.NumberFormat` options, so a currency, percent or compact format
 * reads exactly as it does in the rest of the app. Specs carry the options as
 * plain JSON (`format` + `formatType`), and the chart runtime registers the
 * one expression function that applies them (see `registerVizExpressions`).
 */
// Mapped rather than aliased: an object type (unlike an interface) is
// assignable to Vega-Lite's `format` dictionary.
export type NumberFormat = {
  [Key in keyof Intl.NumberFormatOptions]: Intl.NumberFormatOptions[Key];
};

/** The Vega expression function (and Vega-Lite custom format type) that formats a number. */
export const NUMBER_FORMAT_TYPE = "vizNumber";

const formatters = new Map<string, Intl.NumberFormat>();

/**
 * A value as `format` writes it. A value that isn't a number is written as
 * it is, and Empty as "".
 */
export function formatNumber(
  value: unknown,
  format: NumberFormat = {},
): string {
  if (value === null || value === undefined || value === "") return "";
  const number = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(number)) return String(value);
  const key = JSON.stringify(format);
  let formatter = formatters.get(key);
  if (!formatter) {
    formatter = new Intl.NumberFormat(undefined, format);
    formatters.set(key, formatter);
  }
  return formatter.format(number);
}

/** The `format` / `formatType` pair an axis, text or tooltip channel takes. */
export function numberFormatProps(format: NumberFormat | undefined): {
  format?: NumberFormat;
  formatType?: typeof NUMBER_FORMAT_TYPE;
} {
  return format ? { format, formatType: NUMBER_FORMAT_TYPE } : {};
}

/** The spec config a chart needs to use {@link NUMBER_FORMAT_TYPE}. */
export function numberFormatConfig(
  ...formats: Array<NumberFormat | undefined>
): {
  config?: { customFormatTypes: true };
} {
  return formats.some(Boolean) ? { config: { customFormatTypes: true } } : {};
}

/** A Vega expression writing `valueExpr` in `format`. */
export function formatExpression(
  valueExpr: string,
  format: NumberFormat = {},
): string {
  return `${NUMBER_FORMAT_TYPE}(${valueExpr}, ${JSON.stringify(format)})`;
}
